<template>
  <button
    :type="type"
    :disabled="disabled || loading"
    class="base-button"
    :class="[variantClass, sizeClass, { 'is-loading': loading, 'is-fullwidth': fullWidth }]"
  >
    <Loader2 v-if="loading" class="base-button-spinner" />

    <!-- Left icon -->
    <span v-if="leftIcon && !loading" class="base-button-icon">
      <component :is="leftIcon" class="h-5 w-5" />
    </span>

    <span class="base-button-text" :class="{ 'opacity-0': loading }">
      <slot />
    </span>

    <!-- Right icon -->
    <span v-if="rightIcon && !loading" class="base-button-icon">
      <component :is="rightIcon" class="h-5 w-5" />
    </span>
  </button>
</template>

<script setup lang="ts">
import { computed, type Component } from 'vue'
import { Loader2 } from 'lucide-vue-next'

const props = withDefaults(defineProps<{
  variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'ghost' | 'outline'
  size?: 'sm' | 'md' | 'lg'
  type?: 'button' | 'submit' | 'reset'
  disabled?: boolean
  loading?: boolean
  fullWidth?: boolean
  leftIcon?: Component
  rightIcon?: Component
}>(), {
  variant: 'primary',
  size: 'md',
  type: 'button',
  disabled: false,
  loading: false,
  fullWidth: false
})

const variantClass = computed(() => `base-button--${props.variant}`)
const sizeClass = computed(() => `base-button--${props.size}`)
</script>

<style scoped>
.base-button {
  @apply relative inline-flex items-center justify-center gap-2
         font-semibold rounded-xl
         transition-all duration-200 ease-in-out
         focus:outline-none focus:ring-2 focus:ring-offset-2
         dark:focus:ring-offset-gray-900
         disabled:opacity-60 disabled:cursor-not-allowed
         select-none
         overflow-hidden;
  /* Micro-interaction: slight press */
  transform: translateY(0);
}

.base-button:not(:disabled):active {
  transform: translateY(1px);
}

/* Sizes */
.base-button--sm {
  @apply px-3.5 py-2 text-xs gap-1.5;
}

.base-button--md {
  @apply px-5 py-2.5 text-sm gap-2;
}

.base-button--lg {
  @apply px-6 py-3 text-base gap-2.5;
}

/* Full width */
.base-button.is-fullwidth {
  @apply w-full;
}

/* Primary */
.base-button--primary {
  @apply bg-primary text-white
         hover:bg-primary-hover
         focus:ring-primary/40
         shadow-sm shadow-primary/20
         dark:bg-primary-dark dark:hover:bg-primary-dark-hover
         dark:focus:ring-primary-dark/40
         dark:shadow-primary-dark/20;
}

/* Secondary */
.base-button--secondary {
  @apply bg-secondary text-white
         hover:bg-secondary-hover
         focus:ring-secondary/40
         shadow-sm shadow-secondary/20
         dark:bg-secondary-dark dark:hover:bg-secondary-dark-hover
         dark:focus:ring-secondary-dark/40;
}

/* Success */
.base-button--success {
  @apply bg-success text-white
         hover:bg-success-hover
         focus:ring-success/40
         shadow-sm shadow-success/20
         dark:bg-success-dark dark:hover:bg-success-dark-hover
         dark:focus:ring-success-dark/40;
}

/* Danger */
.base-button--danger {
  @apply bg-danger text-white
         hover:bg-danger-hover
         focus:ring-danger/40
         shadow-sm shadow-danger/20
         dark:bg-danger-dark dark:hover:bg-danger-dark-hover
         dark:focus:ring-danger-dark/40;
}

/* Warning */
.base-button--warning {
  @apply bg-warning text-white
         hover:bg-warning-hover
         focus:ring-warning/40
         shadow-sm shadow-warning/20
         dark:bg-warning-dark dark:hover:bg-warning-dark-hover
         dark:focus:ring-warning-dark/40;
}

/* Info */
.base-button--info {
  @apply bg-info text-white
         hover:bg-info-hover
         focus:ring-info/40
         shadow-sm shadow-info/20
         dark:bg-info-dark dark:hover:bg-info-dark-hover
         dark:focus:ring-info-dark/40;
}

/* Ghost */
.base-button--ghost {
  @apply bg-transparent text-gray-700 dark:text-gray-300
         hover:bg-gray-100 dark:hover:bg-gray-800
         focus:ring-gray-400/40;
}

/* Outline */
.base-button--outline {
  @apply bg-transparent
         border-2 border-primary text-primary
         hover:bg-primary hover:text-white
         focus:ring-primary/40
         dark:border-primary-dark dark:text-primary-dark
         dark:hover:bg-primary-dark dark:hover:text-white;
}

/* Spinner */
.base-button-spinner {
  @apply absolute h-5 w-5 animate-spin;
}

/* Icons */
.base-button-icon {
  @apply inline-flex items-center shrink-0;
}

/* Text */
.base-button-text {
  @apply inline-flex items-center;
}
</style>
